import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useVisibleEnvironments } from "../../components/Environment";
import { useFormat } from "../../lib/useFormat";
import type { InventoryStateView } from "@starsach/shared-types";

/**
 * The sticky context bar of Flow configuration (requirements 8.9, the
 * mock-ups of 2026-09-18): which environment, which profile, which
 * sync state - never scrolls away, so a change is never made in the wrong
 * place by mistake. A live environment gets an amber edge; since #264
 * there is no publish button, and the bar says whether the account has
 * pulled what was saved.
 *
 * The bar's shape never follows its content (27 Sep: the state text grew
 * while an hours bar was dragged and pushed the actions to a second line):
 * the controls and the actions share one line that does not wrap from lg
 * up - the selects give way instead - and a `status` gets a line of its
 * own beneath them, where it truncates rather than wraps.
 */
export function ContextBar({
  environmentId,
  environmentName,
  live,
  onEnvironment,
  children,
  actions,
  status,
}: {
  environmentId: string;
  environmentName?: string | undefined;
  live: boolean;
  /** Where the same screen lives for another environment. */
  onEnvironment: (environmentId: string) => string;
  children?: ReactNode;
  actions?: ReactNode;
  /** State that changes while editing (saving, saved, live): its own line, so a longer message moves nothing. */
  status?: ReactNode;
}) {
  const navigate = useNavigate();
  const envs = useVisibleEnvironments();
  const options = envs.data?.customers ?? [];
  const current = options.find((e) => e.environmentId === environmentId);
  return (
    <div
      className={`sticky top-0 z-10 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-xl border border-neutral-200/80 bg-white px-5 py-3 shadow-sm dark:border-neutral-800 dark:bg-neutral-900 ${live ? "border-l-[6px] border-l-brand" : "border-l-[6px] border-l-neutral-400 dark:border-l-neutral-600"}`}
    >
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3 lg:flex-nowrap">
        <select
          aria-label="Environment"
          value={environmentId}
          onChange={(e) => navigate(onEnvironment(e.target.value))}
          className={`min-h-9 min-w-0 max-w-full truncate rounded-lg border px-3 text-sm font-semibold ${live ? "border-brand bg-orange-50 text-brand-text dark:bg-orange-950/40" : "border-neutral-300 bg-white text-ink dark:border-neutral-700 dark:bg-neutral-900 dark:text-white"}`}
        >
          {options.map((e) => (
            <option key={e.environmentId} value={e.environmentId}>
              {e.friendlyName}
            </option>
          ))}
          {!current && <option value={environmentId}>{environmentName ?? environmentId}</option>}
        </select>
        {children}
      </div>
      {actions && <div className="flex flex-none items-center gap-2">{actions}</div>}
      {status && <div className="flex min-w-0 basis-full items-center gap-x-3 text-sm">{status}</div>}
    </div>
  );
}

/** ADR-0018 section 4: the dropdowns say when the names they show are as of the last settled state. */
export function InventoryStateNote({ state, environmentName }: { state: InventoryStateView | undefined; environmentName: string }) {
  const fmt = useFormat();
  if (!state || state.state !== "changing") return null;
  const who = state.burst?.principal ? ` by ${principalName(state.burst.principal)}` : "";
  const since = state.burst?.firstCallAt ? ` since ${fmt.time(state.burst.firstCallAt)}` : "";
  return (
    <p role="status" className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
      <strong>{environmentName} is changing</strong> - {state.burst?.calls ?? "several"} configuration updates{since}{who}. Names shown are as of{" "}
      {state.settledAt ? fmt.time(state.settledAt) : "the last settled state"}; a promotion into it waits until it settles.
    </p>
  );
}

/** `assumed-role/cfn-deploy-role/i-0abc` reads as the role; a user or a plain role as its last segment. */
export function principalName(arn: string): string {
  const assumed = /assumed-role\/([^/]+)\//.exec(arn);
  return assumed?.[1] ?? arn.split("/").pop() ?? arn;
}
